<template>
  <section class="dashboard-section">
    <i v-if="loading" class="fas fa-spinner loading-big"></i>

    <div v-else-if="error" class="empty-state">
      {{ error }}
    </div>

    <div v-else class="dashboard-frame-wrap">
      <div v-if="frameHint" class="frame-hint">{{ frameHint }}</div>
      <iframe
        :src="iframeSrc"
        :style="frameStyle"
        title="Swiss Money Dashboard"
        class="dashboard-frame"
        allow="fullscreen"
        @load="onFrameLoad"
      />
    </div>
  </section>
</template>

<script>
import { mapState } from 'vuex'
import logger from '../../logger'
import { getToken } from '../../util'
import { getAccess } from '../api/dash'

const KICK_MESSAGE = 'Dashboard aberto em outro dispositivo. Esta sessão foi encerrada.'

export default {
  name: 'Dashboard',
  data () {
    return {
      loading: true,
      error: null,
      iframeSrc: null,
      frameHint: null,
      frameLoadTimer: null,
      socketBound: false,
      joined: false,
      tabId: null,
      scale: 0.9
    }
  },
  computed: {
    ...mapState(['user']),
    frameStyle () {
      const s = this.scale
      const pct = `${(100 / s).toFixed(4)}%`
      return {
        width: pct,
        height: pct,
        transform: `scale(${s})`,
        transformOrigin: 'top left'
      }
    }
  },
  async mounted () {
    if (!this.user) {
      this.$router.push('/')
      return
    }

    this.tabId = this.getOrCreateTabId()
    document.body.style.backgroundColor = '#0b0e11'
    await this.loadAccess()
  },
  beforeDestroy () {
    this.teardownSocket()
    this.clearTimers()
  },
  methods: {
    getOrCreateTabId () {
      const key = 'dash_tab_id'
      try {
        let id = window.sessionStorage.getItem(key)
        if (!id) {
          id = `tab_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`
          window.sessionStorage.setItem(key, id)
        }
        return id
      } catch (e) {
        return `tab_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`
      }
    },
    apiBase () {
      const base = process.env.VUE_APP_API
      if (!base || base === 'undefined') return null
      return String(base).replace(/\/$/, '')
    },
    clearTimers () {
      if (this.frameLoadTimer) {
        clearTimeout(this.frameLoadTimer)
        this.frameLoadTimer = null
      }
    },
    kickSession (message) {
      this.clearTimers()
      this.teardownSocket()
      this.iframeSrc = null
      this.joined = false
      this.error = message || KICK_MESSAGE
    },
    onDashKicked (payload) {
      this.kickSession((payload && payload.message) || KICK_MESSAGE)
    },
    onDashJoined () {
      this.joined = true
    },
    onDashError (payload) {
      this.kickSession((payload && payload.error) || 'Não foi possível abrir a sessão do Dashboard.')
    },
    onSocketConnect () {
      if (this.iframeSrc && !this.error) {
        this.emitJoin()
      }
    },
    emitJoin () {
      const token = getToken()
      if (!token || !this.$socket) return
      this.$socket.emit('dash:join', { token, tabId: this.tabId })
    },
    bindSocket () {
      if (!this.$socket || this.socketBound) return
      this.$socket.on('dash:kicked', this.onDashKicked)
      this.$socket.on('dash:joined', this.onDashJoined)
      this.$socket.on('dash:error', this.onDashError)
      this.$socket.on('connect', this.onSocketConnect)
      this.socketBound = true
    },
    teardownSocket () {
      if (!this.$socket || !this.socketBound) return
      try {
        this.$socket.emit('dash:leave')
        this.$socket.off('dash:kicked', this.onDashKicked)
        this.$socket.off('dash:joined', this.onDashJoined)
        this.$socket.off('dash:error', this.onDashError)
        this.$socket.off('connect', this.onSocketConnect)
      } catch (e) {
        logger.error(e, this)
      }
      this.socketBound = false
    },
    async loadAccess () {
      try {
        this.loading = true
        this.error = null
        this.frameHint = null
        this.clearTimers()

        const api = this.apiBase()
        if (!api) {
          this.error = 'VUE_APP_API não configurada. Reinicie o frontend (npm run dev).'
          return
        }

        const accessData = await getAccess()
        if (!accessData || !accessData.access) {
          this.error = 'Você precisa de uma assinatura Dash ativa para acessar o Dashboard.'
          return
        }

        const token = getToken()
        if (!token) {
          this.error = 'Sessão expirada. Faça login novamente.'
          return
        }

        this.bindSocket()
        this.emitJoin()
        this.iframeSrc = `${api}/dash/app/?jwt=${encodeURIComponent(token)}`

        this.frameHint = 'Carregando...'
        this.frameLoadTimer = setTimeout(() => {
          this.frameHint = null
        }, 5000)
      } catch (e) {
        logger.error(e, this)
        this.error = 'Não foi possível carregar o Dashboard. Tente novamente.'
      } finally {
        this.loading = false
      }
    },
    onFrameLoad () {
      if (this.frameLoadTimer) {
        clearTimeout(this.frameLoadTimer)
        this.frameLoadTimer = null
      }
      this.frameHint = null
    }
  }
}
</script>

<style lang="scss" scoped>
@import "../_asset/scss/_variables";

.dashboard-section {
  display: flex;
  flex-direction: column;
  margin: -$padding-mobile;
  width: calc(100% + #{$padding-mobile} * 2);
  min-height: calc(100vh - #{$menu-height});
  height: calc(100vh - #{$menu-height});
}

.empty-state {
  margin: $padding-mobile;
  text-align: center;
  padding: 48px 16px;
  color: $lp-text-2;
  border: 1px dashed $lp-border;
  border-radius: $lp-radius;
  background: $lp-panel;
}

.dashboard-frame-wrap {
  position: relative;
  flex: 1;
  min-height: 0;
  overflow: hidden;
  background: #0b0e11;
}

.frame-hint {
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  z-index: 1;
  padding: 10px 14px;
  font-size: 0.85em;
  color: $lp-text-2;
  background: rgba(11, 14, 17, 0.92);
  border-bottom: 1px solid $lp-border;
}

.dashboard-frame {
  display: block;
  border: 0;
  background: #0b0e11;
}

@media screen and (min-width: $desktop-width) {
  .dashboard-section {
    margin: -20px -25px;
    width: calc(100% + 50px);
    min-height: 100vh;
    height: 100vh;
  }
}
</style>
